import { useEffect, useRef, useState } from "react";
import { MessageSquareText, X } from "lucide-react";
import { describeSendError, sendMessage } from "../smsApi";
import { formatPhoneInput, PHONE_LINES, toE164 } from "../phone";
import { useCannedMessages } from "../useCannedMessages";
import { useContacts } from "../useContacts";

const MAX_SMS_LENGTH = 1600;

type Props = {
  onClose: () => void;
  onSent: () => void;
  initialTo?: string;
};

export function ComposeModal({ onClose, onSent, initialTo }: Props) {
  const [to, setTo] = useState(
    initialTo ? formatPhoneInput(initialTo.replace(/\D/g, "").slice(-10)) : ""
  );
  const [from, setFrom] = useState(PHONE_LINES[0].number);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cannedOpen, setCannedOpen] = useState(false);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [contactResults, setContactResults] = useState<
    { name: string; phone_number: string; list_type: string }[]
  >([]);

  const toRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const cannedMessages = useCannedMessages();
  const { contacts } = useContacts(0);

  useEffect(() => {
    toRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  function insertCanned(msg: string) {
    setBody(msg);
    setCannedOpen(false);
    bodyRef.current?.focus();
  }

  function handleToChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setError(null);

    const hasLetters = /[a-zA-Z]/.test(val);

    if (hasLetters) {
      setTo(val);
      if (val.length >= 1 && contacts.length > 0) {
        const q = val.toLowerCase();
        const matches = contacts
          .filter((c) => c.name.toLowerCase().includes(q))
          .slice(0, 6);
        setContactResults(matches);
        setShowContactDropdown(matches.length > 0);
      } else {
        setShowContactDropdown(false);
        setContactResults([]);
      }
    } else {
      const formatted = formatPhoneInput(val);
      setTo(formatted);
      const digits = val.replace(/\D/g, "");
      if (digits.length >= 3 && contacts.length > 0) {
        const matches = contacts
          .filter((c) => c.phone_number.includes(digits))
          .slice(0, 6);
        setContactResults(matches);
        setShowContactDropdown(matches.length > 0);
      } else {
        setShowContactDropdown(false);
        setContactResults([]);
      }
    }
  }

  function selectContact(c: { phone_number: string }) {
    const digits = c.phone_number.replace(/\D/g, "").slice(-10);
    setTo(formatPhoneInput(digits));
    setShowContactDropdown(false);
    setContactResults([]);
    bodyRef.current?.focus();
  }

  async function handleSend() {
    setError(null);
    const e164 = toE164(to);
    if (!e164) {
      setError("Enter a valid 10-digit phone number");
      return;
    }
    if (!body.trim()) {
      setError("Message cannot be empty");
      return;
    }

    setSending(true);
    try {
      await sendMessage({ to: e164, from, body: body.trim() });
      onSent();
      onClose();
    } catch (e) {
      setError(describeSendError(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md rounded-xl border border-tribal-border bg-tribal-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-tribal-border px-5 py-4">
          <h2 className="text-lg font-semibold text-white">New Message</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-tribal-muted transition-colors hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="relative">
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              To
            </label>
            <input
              ref={toRef}
              type="text"
              value={to}
              onChange={handleToChange}
              onBlur={() => setTimeout(() => setShowContactDropdown(false), 200)}
              placeholder="Name or (555) 123-4567"
              className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
            />
            {showContactDropdown && contactResults.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-xl border border-tribal-border bg-tribal-surface shadow-xl">
                {contactResults.map((c) => (
                  <button
                    key={c.phone_number}
                    type="button"
                    onMouseDown={() => selectContact(c)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-white/5"
                  >
                    <span className="text-sm text-white">{c.name}</span>
                    <span className="text-[10px] text-tribal-muted">
                      {formatPhoneInput(c.phone_number.replace(/\D/g, "").slice(-10))}
                    </span>
                    <span className="ml-auto text-[10px] text-tribal-muted">
                      {c.list_type}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              From
            </label>
            <select
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-gray-300 focus:border-tribal-red focus:outline-none"
            >
              {PHONE_LINES.map((line) => (
                <option key={line.number} value={line.number}>
                  {line.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-xs font-medium text-tribal-muted">
                Message
              </label>
              {cannedMessages.length > 0 && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setCannedOpen((o) => !o)}
                    className="flex items-center gap-1 rounded-lg border border-tribal-border bg-tribal-bg px-2 py-1 text-[11px] text-tribal-muted transition-colors hover:bg-white/5 hover:text-white"
                  >
                    <MessageSquareText className="h-3 w-3" />
                    Quick replies
                  </button>
                  {cannedOpen && (
                    <div className="absolute right-0 top-full z-10 mt-1 w-64 overflow-hidden rounded-xl border border-tribal-border bg-tribal-surface shadow-xl">
                      <p className="border-b border-tribal-border px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-tribal-muted">
                        Quick Replies
                      </p>
                      <div className="max-h-48 divide-y divide-tribal-border overflow-y-auto">
                        {cannedMessages.map((msg, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => insertCanned(msg)}
                            className="w-full px-3 py-2 text-left text-sm text-gray-200 transition-colors hover:bg-white/5"
                          >
                            {msg.length > 80 ? msg.slice(0, 80) + "…" : msg}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message…"
              rows={4}
              className="w-full resize-none rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
            />
            <div
              className={`mt-1 text-right text-[10px] ${
                body.length > MAX_SMS_LENGTH ? "text-red-400" : "text-tribal-muted"
              }`}
            >
              {body.length}/{MAX_SMS_LENGTH}
            </div>
          </div>

          {error && (
            <p className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-tribal-border px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-tribal-muted transition-colors hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || body.length > MAX_SMS_LENGTH}
            className="rounded-lg bg-tribal-red px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send Message"}
          </button>
        </div>
      </div>
    </div>
  );
}
